import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Network,
  Radio,
  Battery,
  Signal,
  RefreshCw,
  Play,
  Pause,
  Smartphone,
  Router,
  Server,
  Globe,
  Info,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { MeshNode, MeshConnection, NodeType, NodeStatus } from '../types';

// Demo node types configuration
const nodeTypeConfig: Record<NodeType, { icon: typeof Radio; color: string; label: string }> = {
  hub: { icon: Server, color: '#dc3329', label: 'Hub' },
  relay: { icon: Router, color: '#f59e0b', label: 'Relay' },
  endpoint: { icon: Smartphone, color: '#22c55e', label: 'Endpoint' },
  gateway: { icon: Globe, color: '#3b82f6', label: 'Gateway' },
};

const statusColors: Record<NodeStatus, string> = {
  online: '#22c55e',
  offline: '#6b7280',
  degraded: '#f59e0b',
  unknown: '#9ca3af',
};

// Generate demo mesh network data
function generateDemoNetwork(): { nodes: MeshNode[]; connections: MeshConnection[] } {
  const nodes: MeshNode[] = [
    // Central hub
    { id: 'hub-1', name: 'Central Hub', type: 'hub', status: 'online', x: 400, y: 250, signalStrength: 100, batteryLevel: 100, connectedNodes: ['relay-1', 'relay-2', 'relay-3', 'gateway-1'], lastSeen: Date.now() },

    // Relays
    { id: 'relay-1', name: 'Relay Alpha', type: 'relay', status: 'online', x: 200, y: 150, signalStrength: 85, batteryLevel: 72, connectedNodes: ['hub-1', 'endpoint-1', 'endpoint-2'], lastSeen: Date.now() },
    { id: 'relay-2', name: 'Relay Beta', type: 'relay', status: 'online', x: 600, y: 150, signalStrength: 78, batteryLevel: 45, connectedNodes: ['hub-1', 'endpoint-3', 'endpoint-4'], lastSeen: Date.now() },
    { id: 'relay-3', name: 'Relay Gamma', type: 'relay', status: 'degraded', x: 400, y: 400, signalStrength: 52, batteryLevel: 23, connectedNodes: ['hub-1', 'endpoint-5', 'endpoint-6'], lastSeen: Date.now() - 30000 },

    // Gateway
    { id: 'gateway-1', name: 'Internet Gateway', type: 'gateway', status: 'online', x: 650, y: 300, signalStrength: 95, batteryLevel: 100, connectedNodes: ['hub-1'], lastSeen: Date.now() },

    // Endpoints
    { id: 'endpoint-1', name: 'Smith Family', type: 'endpoint', status: 'online', x: 80, y: 100, signalStrength: 72, batteryLevel: 88, connectedNodes: ['relay-1'], lastSeen: Date.now() },
    { id: 'endpoint-2', name: 'Johnson Home', type: 'endpoint', status: 'online', x: 150, y: 280, signalStrength: 65, batteryLevel: 55, connectedNodes: ['relay-1'], lastSeen: Date.now() },
    { id: 'endpoint-3', name: 'Community Center', type: 'endpoint', status: 'online', x: 700, y: 80, signalStrength: 81, batteryLevel: 100, connectedNodes: ['relay-2'], lastSeen: Date.now() },
    { id: 'endpoint-4', name: 'Fire Station', type: 'endpoint', status: 'online', x: 720, y: 200, signalStrength: 90, batteryLevel: 100, connectedNodes: ['relay-2'], lastSeen: Date.now() },
    { id: 'endpoint-5', name: 'School Shelter', type: 'endpoint', status: 'offline', x: 280, y: 480, signalStrength: 0, batteryLevel: 0, connectedNodes: [], lastSeen: Date.now() - 3600000 },
    { id: 'endpoint-6', name: 'Hospital Wing', type: 'endpoint', status: 'online', x: 520, y: 450, signalStrength: 48, batteryLevel: 34, connectedNodes: ['relay-3'], lastSeen: Date.now() },
  ];

  const connections: MeshConnection[] = [
    // Hub connections
    { id: 'conn-1', sourceId: 'hub-1', targetId: 'relay-1', strength: 85, latency: 12, isActive: true },
    { id: 'conn-2', sourceId: 'hub-1', targetId: 'relay-2', strength: 78, latency: 15, isActive: true },
    { id: 'conn-3', sourceId: 'hub-1', targetId: 'relay-3', strength: 52, latency: 45, isActive: true },
    { id: 'conn-4', sourceId: 'hub-1', targetId: 'gateway-1', strength: 95, latency: 5, isActive: true },

    // Relay-1 connections
    { id: 'conn-5', sourceId: 'relay-1', targetId: 'endpoint-1', strength: 72, latency: 18, isActive: true },
    { id: 'conn-6', sourceId: 'relay-1', targetId: 'endpoint-2', strength: 65, latency: 22, isActive: true },

    // Relay-2 connections
    { id: 'conn-7', sourceId: 'relay-2', targetId: 'endpoint-3', strength: 81, latency: 14, isActive: true },
    { id: 'conn-8', sourceId: 'relay-2', targetId: 'endpoint-4', strength: 90, latency: 8, isActive: true },

    // Relay-3 connections
    { id: 'conn-9', sourceId: 'relay-3', targetId: 'endpoint-5', strength: 0, latency: 0, isActive: false },
    { id: 'conn-10', sourceId: 'relay-3', targetId: 'endpoint-6', strength: 48, latency: 55, isActive: true },
  ];

  return { nodes, connections };
}

export function MeshNetwork() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [network, setNetwork] = useState<{ nodes: MeshNode[]; connections: MeshConnection[] }>({ nodes: [], connections: [] });
  const [selectedNode, setSelectedNode] = useState<MeshNode | null>(null);
  const [isSimulating, setIsSimulating] = useState(true);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const animationRef = useRef<number>(0);
  const pulseRef = useRef(0);

  useEffect(() => {
    const data = generateDemoNetwork();
    setNetwork(data);
  }, []);

  // Draw network visualization
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Clear canvas
    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw grid
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 1;
    const gridSize = 40;
    for (let x = 0; x < canvas.width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    for (let y = 0; y < canvas.height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }

    // Calculate scale based on container size
    const scaleX = canvas.width / 800;
    const scaleY = canvas.height / 550;
    const scale = Math.min(scaleX, scaleY, 1);
    const offsetX = (canvas.width - 800 * scale) / 2;
    const offsetY = (canvas.height - 550 * scale) / 2;

    // Draw connections
    network.connections.forEach((conn) => {
      const source = network.nodes.find((n) => n.id === conn.sourceId);
      const target = network.nodes.find((n) => n.id === conn.targetId);
      if (!source || !target) return;

      const x1 = source.x * scale + offsetX;
      const y1 = source.y * scale + offsetY;
      const x2 = target.x * scale + offsetX;
      const y2 = target.y * scale + offsetY;

      // Connection line
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);

      if (conn.isActive) {
        const alpha = 0.3 + (conn.strength / 100) * 0.5;
        ctx.strokeStyle = `rgba(34, 197, 94, ${alpha})`;
        ctx.lineWidth = 2 + (conn.strength / 100) * 2;

        // Animated pulse along connection
        if (isSimulating) {
          const pulsePos = (pulseRef.current % 100) / 100;
          const px = x1 + (x2 - x1) * pulsePos;
          const py = y1 + (y2 - y1) * pulsePos;

          const gradient = ctx.createRadialGradient(px, py, 0, px, py, 10);
          gradient.addColorStop(0, 'rgba(34, 197, 94, 0.8)');
          gradient.addColorStop(1, 'rgba(34, 197, 94, 0)');
          ctx.fillStyle = gradient;
          ctx.fillRect(px - 10, py - 10, 20, 20);
        }
      } else {
        ctx.strokeStyle = 'rgba(107, 114, 128, 0.3)';
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 5]);
      }

      ctx.stroke();
      ctx.setLineDash([]);
    });

    // Draw nodes
    network.nodes.forEach((node) => {
      const x = node.x * scale + offsetX;
      const y = node.y * scale + offsetY;
      const config = nodeTypeConfig[node.type];
      const statusColor = statusColors[node.status];
      const isHovered = hoveredNode === node.id;
      const isSelected = selectedNode?.id === node.id;

      // Node glow
      if (node.status === 'online' && isSimulating) {
        const glowSize = 30 + Math.sin(pulseRef.current / 20) * 5;
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, glowSize);
        gradient.addColorStop(0, `${config.color}40`);
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, glowSize, 0, Math.PI * 2);
        ctx.fill();
      }

      // Node circle
      const radius = isHovered || isSelected ? 22 : 18;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle = '#1f2937';
      ctx.fill();
      ctx.strokeStyle = config.color;
      ctx.lineWidth = isSelected ? 4 : 2;
      ctx.stroke();

      // Status indicator
      ctx.beginPath();
      ctx.arc(x + radius - 4, y - radius + 4, 5, 0, Math.PI * 2);
      ctx.fillStyle = statusColor;
      ctx.fill();

      // Node label
      ctx.fillStyle = '#e5e7eb';
      ctx.font = '12px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(node.name, x, y + radius + 16);

      // Type indicator
      ctx.fillStyle = config.color;
      ctx.font = 'bold 10px Inter, sans-serif';
      ctx.fillText(config.label.toUpperCase(), x, y + 4);
    });

    // Update pulse for animation
    if (isSimulating) {
      pulseRef.current += 0.5;
    }

    animationRef.current = requestAnimationFrame(draw);
  }, [network, selectedNode, hoveredNode, isSimulating]);

  useEffect(() => {
    draw();
    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [draw]);

  // Handle canvas click for node selection
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const scaleX = canvas.width / 800;
    const scaleY = canvas.height / 550;
    const scale = Math.min(scaleX, scaleY, 1);
    const offsetX = (canvas.width - 800 * scale) / 2;
    const offsetY = (canvas.height - 550 * scale) / 2;

    const clickedNode = network.nodes.find((node) => {
      const nodeX = node.x * scale + offsetX;
      const nodeY = node.y * scale + offsetY;
      const distance = Math.sqrt((x - nodeX) ** 2 + (y - nodeY) ** 2);
      return distance < 25;
    });

    setSelectedNode(clickedNode || null);
  };

  // Handle mouse move for hover effect
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const scaleX = canvas.width / 800;
    const scaleY = canvas.height / 550;
    const scale = Math.min(scaleX, scaleY, 1);
    const offsetX = (canvas.width - 800 * scale) / 2;
    const offsetY = (canvas.height - 550 * scale) / 2;

    const hoveredNode = network.nodes.find((node) => {
      const nodeX = node.x * scale + offsetX;
      const nodeY = node.y * scale + offsetY;
      const distance = Math.sqrt((x - nodeX) ** 2 + (y - nodeY) ** 2);
      return distance < 25;
    });

    setHoveredNode(hoveredNode?.id || null);
    canvas.style.cursor = hoveredNode ? 'pointer' : 'default';
  };

  // Simulate network changes
  useEffect(() => {
    if (!isSimulating) return;

    const interval = setInterval(() => {
      setNetwork((prev) => ({
        ...prev,
        nodes: prev.nodes.map((node) => ({
          ...node,
          signalStrength: Math.max(0, Math.min(100, node.signalStrength + (Math.random() - 0.5) * 10)),
          batteryLevel: node.type === 'gateway' ? 100 : Math.max(0, node.batteryLevel - Math.random() * 0.1),
          lastSeen: node.status === 'online' ? Date.now() : node.lastSeen,
        })),
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, [isSimulating]);

  const stats = {
    total: network.nodes.length,
    online: network.nodes.filter((n) => n.status === 'online').length,
    offline: network.nodes.filter((n) => n.status === 'offline').length,
    degraded: network.nodes.filter((n) => n.status === 'degraded').length,
    activeConnections: network.connections.filter((c) => c.isActive).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Network className="w-6 h-6 text-emergency-400" />
            Mesh Network Topology
          </h1>
          <p className="text-gray-400 mt-1">Visualize emergency communication network</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsSimulating(!isSimulating)}
            className={`btn ${isSimulating ? 'btn-warning' : 'btn-safe'}`}
          >
            {isSimulating ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {isSimulating ? 'Pause' : 'Start'}
          </button>
          <button
            onClick={() => setNetwork(generateDemoNetwork())}
            className="btn-secondary"
          >
            <RefreshCw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-white">{stats.total}</div>
          <div className="text-xs text-gray-400">Total Nodes</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-safe-400">{stats.online}</div>
          <div className="text-xs text-gray-400">Online</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-warning-400">{stats.degraded}</div>
          <div className="text-xs text-gray-400">Degraded</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-emergency-400">{stats.offline}</div>
          <div className="text-xs text-gray-400">Offline</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-blue-400">{stats.activeConnections}</div>
          <div className="text-xs text-gray-400">Active Links</div>
        </div>
      </div>

      {/* Network Visualization */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div className="card overflow-hidden">
            <div className="p-3 border-b border-gray-700 flex items-center justify-between">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Radio className="w-4 h-4 text-safe-400" />
                Network Map
                {isSimulating && (
                  <span className="flex items-center gap-1 text-xs text-safe-400">
                    <span className="w-2 h-2 bg-safe-400 rounded-full animate-pulse" />
                    Live
                  </span>
                )}
              </h3>
            </div>
            <div ref={containerRef} className="h-[400px] lg:h-[500px]">
              <canvas
                ref={canvasRef}
                onClick={handleCanvasClick}
                onMouseMove={handleMouseMove}
                className="w-full h-full"
              />
            </div>
          </div>
        </div>

        {/* Node Details / Legend */}
        <div className="space-y-4">
          {/* Legend */}
          <div className="card p-4">
            <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Info className="w-4 h-4 text-gray-400" />
              Legend
            </h3>
            <div className="space-y-2">
              {(Object.entries(nodeTypeConfig) as [NodeType, typeof nodeTypeConfig.hub][]).map(([type, config]) => {
                const Icon = config.icon;
                return (
                  <div key={type} className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${config.color}20`, border: `2px solid ${config.color}` }}
                    >
                      <Icon className="w-3 h-3" style={{ color: config.color }} />
                    </div>
                    <span className="text-sm text-gray-300">{config.label}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-700 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-safe-500" />
                <span className="text-sm text-gray-300">Online</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-warning-500" />
                <span className="text-sm text-gray-300">Degraded</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gray-500" />
                <span className="text-sm text-gray-300">Offline</span>
              </div>
            </div>
          </div>

          {/* Selected Node Details */}
          <AnimatePresence mode="wait">
            {selectedNode && (
              <motion.div
                key={selectedNode.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="card p-4"
              >
                <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                  {(() => {
                    const Icon = nodeTypeConfig[selectedNode.type].icon;
                    return <Icon className="w-4 h-4" style={{ color: nodeTypeConfig[selectedNode.type].color }} />;
                  })()}
                  {selectedNode.name}
                </h3>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Status</span>
                    <span className={`badge ${
                      selectedNode.status === 'online' ? 'badge-safe' :
                      selectedNode.status === 'degraded' ? 'badge-warning' :
                      'bg-gray-700 text-gray-400'
                    }`}>
                      {selectedNode.status}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Type</span>
                    <span className="text-sm text-white capitalize">{selectedNode.type}</span>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-400 flex items-center gap-1">
                        <Signal className="w-3 h-3" /> Signal
                      </span>
                      <span className="text-sm text-white">{Math.round(selectedNode.signalStrength)}%</span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          selectedNode.signalStrength >= 70 ? 'bg-safe-500' :
                          selectedNode.signalStrength >= 40 ? 'bg-warning-500' :
                          'bg-emergency-500'
                        }`}
                        style={{ width: `${selectedNode.signalStrength}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-400 flex items-center gap-1">
                        <Battery className="w-3 h-3" /> Battery
                      </span>
                      <span className="text-sm text-white">{Math.round(selectedNode.batteryLevel)}%</span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          selectedNode.batteryLevel >= 50 ? 'bg-safe-500' :
                          selectedNode.batteryLevel >= 20 ? 'bg-warning-500' :
                          'bg-emergency-500'
                        }`}
                        style={{ width: `${selectedNode.batteryLevel}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Connections</span>
                    <span className="text-sm text-white">{selectedNode.connectedNodes.length}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Last Seen</span>
                    <span className="text-sm text-white">
                      {selectedNode.status === 'online'
                        ? 'Just now'
                        : new Date(selectedNode.lastSeen).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {!selectedNode && (
            <div className="card p-4 text-center">
              <Radio className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Click on a node to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="card p-4 border-l-4 border-l-blue-500">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-white">About Mesh Networks</h4>
            <p className="text-sm text-gray-400 mt-1">
              This visualization demonstrates how mesh networks enable decentralized communication during emergencies.
              Each node can relay messages to others, creating resilient pathways even when some nodes go offline.
              In a real disaster scenario, these nodes could be LoRa radios, smartphones, or dedicated communication devices.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
